import { RiskMetricId } from '@/constants/ReportDashboard/api-enums'
import { RADashboardCardId } from '@/constants/ReportDashboard/cards-enums'
import { FADashboardSubtype, FADashboardType } from '@/constants/ReportDashboard/dashboards/dashboard-types'
import { GfrItem } from '@/constants/ReportDashboard/gfr-items'
import { ReportItem } from '@/constants/ReportDashboard/report-items'
import { HoldingsMetric, ShowContributors } from '@/contexts/HoldingsTabContext'

export interface RiskMetricsView {
  id: string
  name: string
  covarianceMatrix: string
  varConfidenceLevel: number
  varTimePeriodValue: number
  varTimePeriodUnit: VaRPeriod
}

export interface RiskMetricsSummaryPrefs {
  visibleMetricIds: RiskMetricId[]
  views?: RiskMetricsView[]
  activeViewId?: string
}

export interface TopBottomPrefs {
  metric: HoldingsMetric
  showContributors: ShowContributors
  contributorCount: 5 | 10 | 15
}

export enum RiskVisualizationViewMode {
  AllHoldings = 'allHoldings',
  TopBottom = 'topBottom'
}

export interface RiskVisualizationPrefs {
  viewMode: RiskVisualizationViewMode
}

export interface PortfolioHoldingsPrefs {
  columnState?: Record<string, unknown>[]
  filterModel?: Record<string, unknown>
}

export const cardPrefsContextKey = (dashboardName: string) =>
  `reportDashboard.${dashboardName.replace(/ /g, '_')}`

export interface TrailingRiskCardPrefs {
  selectedDays: string
  selectedChart: string
  showTable: boolean
}

export interface FactorLensPrefs {
  selectedDataType?: string
  contributionsShown?: boolean
  waterfallShown?: boolean
  factorTableColumnState?: Record<string, unknown>[]
  factorTableFilterModel?: Record<string, unknown>
  topContributorsColumnState?: Record<string, unknown>[]
  bottomContributorsColumnState?: Record<string, unknown>[]
}

export interface DailyReturnChartPrefs {
  selectedChart: string
  hiddenSeries?: string[]
}

export interface FactorAttributionPrefs {
  view: string
  hiddenFactors?: string[]
  columnOrder?: string[]
}

export interface AttributionHoldingsPrefs {
  columnState?: Record<string, unknown>[]
  filterModel?: Record<string, unknown>
}

export interface RADashboardCardPrefs {
  dashboardName: string
  riskMetricsSummary?: RiskMetricsSummaryPrefs
  holdingsTopBottomTable?: TopBottomPrefs
  holdingsRiskVisualization?: RiskVisualizationPrefs
  holdingsPortfolio?: PortfolioHoldingsPrefs
  trailingRisk?: TrailingRiskCardPrefs
  factorLens?: FactorLensPrefs
  dailyReturn?: DailyReturnChartPrefs
  factorAttribution?: FactorAttributionPrefs
  attributionHoldings?: AttributionHoldingsPrefs
}

export interface CardDimension {
  width: number
  height: number
}

export interface RADashboardTheme {
  fontFamily: string
  background: string
  cardBackground: string
  inputBackground: string
  headerBackground: string
  textPrimary: string
  textSecondary: string
  border: string
  subheaderBackground: string
  buttonBackground: string
  buttonText: string
  boxShadowColor: string
  toggleBackground: string
  toggleBorderColor: string
  togglePrimary: string
  configBoxShadowColor: string
  benchmarkBoxShadowColor: string
  runButtonBackground: string
  runButtonColor: string
  runButtonBoxShadowColor: string
  tabBackground: string
  tabSelectedBackground: string
  factorLensGridBoxColor: string
  factorLensGridBoxBorder: string
  factorLensGridPrimaryColor: string
  factorLensTotalColor: string
  factorLensTotalBorder: string
  factorLensTotalSelected: string
  factorLensSpecificColor: string
  factorLensSpecificBorder: string
  factorLensSpecificSelected: string
}

export interface RADashboardLayout {
  suggestedPortfolioType: string[]
  tabsOrder: string[]
  initialTab: string
  tabLayouts: Record<string, TabLayout>
  tabSettings: Record<string, TabSetting>
  cardSettings: Record<string, CardSettings>
}

export interface FADashboardLayout {
  suggestedPortfolioType: string[]
  dashboardType: FADashboardType
  dashboardSubtype: FADashboardSubtype
  cardsOrder: (RADashboardCardId | string)[]
  cardSettings: Record<string, CardSettings>
}

export interface RADashboardTabs {
  selectedTab: string
  setSelectedTab: (tabKey: string) => void
}

export interface CardProps {
  key: string
  cardId: RADashboardCardId
  w: number
  h: number
  gridUnitSize: number
  cardSettings: CardSettings
  cardPrefs?: RADashboardCardPrefs
  onCardPrefsChange?: (newPrefs: RADashboardCardPrefs) => void
}

export interface RADashboardGrid
  extends Pick<CardProps, 'cardPrefs' | 'onCardPrefsChange'> {
  layout: RADashboardLayout
  selectedTab: string
  dashboardType: FADashboardType
}

export interface TabLayout {
  cardsOrder: (RADashboardCardId | string)[]
  //cardsShown: Record<string, boolean>;
}

export interface TabSetting {
  label: string
}

export interface CardSettings {
  size: string
}

export interface FactorContrib {
  "data_set": string,
  "level1": string,
  "level2": string,
  "level3": string,
  "level4": string,
  "level5": string,
  "factor_category": string,
  "factor_sub_category": string,
  "factor_name": string,
  "currency_code": string,
  "weighted_exposure": number | null,
  "risk_contrib": number,
  "factor_risk": number | null,
}

export interface FactorPortContrib extends FactorContrib {
  "factor_category_sort": number,
  "factor_sub_category_sort": number,
  "factor_name_sort": number,
  "axsid": number,
  "security_id": string,
  "description": string,
  "security_risk": number  
}

export function isPortContrib(factor: FactorContrib | FactorPortContrib): factor is FactorPortContrib {
  return "security_id" in factor;
};

export interface FactorLensEndSecurity {
  axsid: number
  security_id: string
  description: string
  security_risk: number
  weighted_exposure: number
  risk_contrib: number
  rank_type: 'Top' | 'Bottom'
}

export interface TopBottomSecurities {
  top: FactorLensEndSecurity[]
  bottom: FactorLensEndSecurity[]
}
export interface LayoutEditorProps {
  layout: RADashboardLayout | undefined
  isLocked: boolean
  setLayout: (newLayout: RADashboardLayout) => void
  dashboardType: FADashboardType
}

export enum VaRPeriod {
  Days = 'Day',
  Months = 'Month',
  Years = 'Year',
}

export interface VaRInfo {
  rate: number
  length: number
  period: VaRPeriod
}

export interface HoldingsSecurityDetails {
  identifier: string
  holdings: number | null
  issuer: string | null // description?
  securityRisk: number | null
  securityRiskContrib: number | null // security risk contrib?
  factorRiskContrib: number | null // model risk contrib?
  notionalWeight: number | null
  parWeight: number | null
  coupon: number | null // coup
  maturity: string | null // 
  price: number | null // price
  marketValue: number | null // mkval
  marketValueMillions: number | null // mkval in millions
  duration: number | null //edur
  sector: string | null // sectornm
  country: string | null // countrynm
  currency: string | null // currency
  rating: string | null // 
  yearsToMaturity: number | null // yrtomatu
  spread: number | null // spd
  dv01: number | null // dv01
  convexity: number | null // convex
  yieldToMaturity: number | null // yld
  [GfrItem.CountryRiskContrib]: number | null
  [GfrItem.FundamentalRiskContrib]: number | null
  [GfrItem.IndustryRiskContrib]: number | null
  [GfrItem.RegionRiskContrib]: number | null
  [GfrItem.OtherSpreadRiskContrib]: number | null
  [GfrItem.QualityRiskContrib]: number | null
  [GfrItem.SectorRiskContrib]: number | null
  [GfrItem.YieldCurveRiskContrib]: number | null
  [GfrItem.AltRiskContrib]: number | null
  [GfrItem.CommodityRiskContrib]: number | null
  [GfrItem.SpecificRiskContrib]: number | null
  [GfrItem.ModelRiskContrib]: number | null
  [GfrItem.CurrencyRiskContrib]: number | null
  [GfrItem.SecurityRiskContrib]: number | null
  [GfrItem.SecurityRisk]: number | null
  [GfrItem.ModelRisk]: number | null
  [GfrItem.SpecificRisk]: number | null
  [ReportItem.SpreadDuration]: number | null
  [ReportItem.YieldToWorst]: number | null
  [ReportItem.DividendYield]: number | null
  [ReportItem.AverageLife]: number | null
  [ReportItem.Beta]: number | null
  [ReportItem.BookToPrice]: number | null
  [ReportItem.BookValue]: number | null
  [ReportItem.CashFlowYield]: number | null
  [ReportItem.CurrentYield]: number | null
  [ReportItem.EPRatio]: number | null
  [ReportItem.EarningsPerShare]: number | null
  [ReportItem.EffectiveD2]: number | null
  [ReportItem.EffectiveD3]: number | null
  [ReportItem.DurContributionPct]: number | null
  [ReportItem.DurContributionYrs]: number | null
  [ReportItem.EffectiveSpreadDuration]: number | null
  [ReportItem.SpreadDurContributionPct]: number | null
  [ReportItem.SpreadDurContributionYrs]: number | null
  [ReportItem.GainLoss]: number | null
  [ReportItem.MacaulayDuration]: number | null
  [ReportItem.MarketCap]: number | null
  [ReportItem.MarketCapLog]: number | null
  [ReportItem.MarketValuePar]: number | null
  [ReportItem.ModifiedDuration]: number | null
  [ReportItem.Momentum]: number | null
  [ReportItem.NotionalValue]: number | null
  [ReportItem.PBRatio]: number | null
  [ReportItem.PERatio]: number | null
  [ReportItem.PctMarketValue]: number | null
  [ReportItem.SPQuality]: string | null
  [ReportItem.SecurityCount]: string | null
  [ReportItem.BaseTotalAccruedInterest]: number | null
  [ReportItem.TrailingEPS]: number | null
  [ReportItem.Volatility]: number | null
  [ReportItem.YearsToWorst]: number | null
  [ReportItem.MSCICountry]: string | null
  [ReportItem.GICSIndustryGrp]: string | null
  [ReportItem.GICSIndustry]: string | null
  [ReportItem.GICSSector]: string | null
  [ReportItem.GICSSubIndustry]: string | null
  [ReportItem.ReturnOnEquity]: string | null
  [ReportItem.DatedDate]: string | null
  [ReportItem.KeyRate1Month]: string | null
  [ReportItem.KeyRate1Year]: string | null
  [ReportItem.KeyRate2Year]: string | null
  [ReportItem.KeyRate3Month]: string | null
  [ReportItem.KeyRate3Year]: string | null
  [ReportItem.KeyRate4Year]: string | null
  [ReportItem.KeyRate5Year]: string | null
  [ReportItem.KeyRate6Month]: string | null
  [ReportItem.KeyRate6Year]: string | null
  [ReportItem.KeyRate7Year]: string | null
  [ReportItem.KeyRate8Year]: string | null
  [ReportItem.KeyRate9Month]: string | null
  [ReportItem.KeyRate9Year]: string | null
  [ReportItem.KeyRate10Year]: string | null
  [ReportItem.KeyRate15Year]: string | null
  [ReportItem.KeyRate0Year]: string | null
  [ReportItem.KeyRate25Year]: string | null
  [ReportItem.KeyRate30Year]: string | null
  [ReportItem.AssetType]: string | null
  [ReportItem.AssetClass]: string | null
  [ReportItem.CountryofIncorp]: string | null
  [ReportItem.CountryofRisk]: string | null
  [ReportItem.BaseCurrencyName]: string | null
  [ReportItem.CUSIP9]: string | null
  [ReportItem.ExchangeTicker]: string | null
  [ReportItem.ISIN12]: string | null
  [ReportItem.SEDOL7]: string | null
}
